import { ShieldCheck, CheckCircle2, AlertCircle, Plug, Lock, Layers } from "lucide-react";
import { useLang } from "@/contexts/LanguageContext";

/**
 * Phase 4 — Permission Center UI Stub.
 *
 * Pure UI/product-information component. NO real integrations, NO OAuth,
 * NO external calls, NO backend writes. Embedded inside the existing
 * Settings page so we don't add new routes or change the app shell.
 *
 * Shape (5 blocks):
 *   1. Current Mode      — what Yusi can do right now
 *   2. Approval Required — actions that must always need user approval
 *   3. Future Connections— platforms Yusi will be able to talk to later
 *   4. Permission Levels — the 4-tier mental model for future agent mode
 *   5. Security & Trust  — closing reassurance block
 */
export default function YusiPermissionsPanel() {
  const { t } = useLang();

  // Static keys for the 4 list-based blocks. Translations live in i18n.js.
  const currentItems = [
    "perm.current.item1",
    "perm.current.item2",
    "perm.current.item3",
    "perm.current.item4",
    "perm.current.item5",
    "perm.current.item6",
    "perm.current.item7",
    "perm.current.item8",
  ];
  const approvalItems = [
    "perm.approval.item1",
    "perm.approval.item2",
    "perm.approval.item3",
    "perm.approval.item4",
    "perm.approval.item5",
    "perm.approval.item6",
    "perm.approval.item7",
  ];
  const futureCards = [
    "perm.future.card.gbp",
    "perm.future.card.website",
    "perm.future.card.wordpress",
    "perm.future.card.webflowEtc",
    "perm.future.card.instaMeta",
    "perm.future.card.msgChannels",
    "perm.future.card.gsc",
  ];
  const levels = [
    "perm.level.prepareOnly",
    "perm.level.askApproval",
    "perm.level.applyApproved",
    "perm.level.autoComplete",
  ];

  return (
    <section
      id="yusi-permissions"
      data-testid="yusi-permissions-panel"
      className="glass-strong rounded-3xl p-7 space-y-8 scroll-mt-24"
    >
      <header className="flex items-start gap-3">
        <div className="rounded-xl bg-cyan-500/15 border border-cyan-500/30 p-2">
          <ShieldCheck size={18} className="text-cyan-300" strokeWidth={2} />
        </div>
        <div>
          <h2 className="font-display text-xl font-semibold text-white">
            {t("perm.title")}
          </h2>
          <p className="text-sm text-slate-400 mt-1 max-w-2xl leading-relaxed">
            {t("perm.intro")}
          </p>
        </div>
      </header>

      {/* 1. Current Mode */}
      <Block
        testid="perm-block-current"
        icon={<CheckCircle2 size={14} className="text-emerald-300" />}
        title={t("perm.current.title")}
        description={t("perm.current.explainer")}
      >
        <ul className="grid sm:grid-cols-2 gap-2">
          {currentItems.map((k) => (
            <li
              key={k}
              className="flex items-start gap-2 text-[13px] text-slate-200 leading-relaxed"
            >
              <span className="mt-1 inline-block w-1 h-1 rounded-full bg-emerald-400 shrink-0" />
              <span>{t(k)}</span>
            </li>
          ))}
        </ul>
      </Block>

      {/* 2. Approval Required */}
      <Block
        testid="perm-block-approval"
        icon={<AlertCircle size={14} className="text-amber-300" />}
        title={t("perm.approval.title")}
        description={t("perm.approval.explainer")}
        accent="amber"
      >
        <ul className="grid sm:grid-cols-2 gap-2">
          {approvalItems.map((k) => (
            <li
              key={k}
              className="flex items-start gap-2 text-[13px] text-slate-200 leading-relaxed"
            >
              <span className="mt-1 inline-block w-1 h-1 rounded-full bg-amber-400 shrink-0" />
              <span>{t(k)}</span>
            </li>
          ))}
        </ul>
      </Block>

      {/* 3. Future Connections */}
      <Block
        testid="perm-block-future"
        icon={<Plug size={14} className="text-slate-300" />}
        title={t("perm.future.title")}
      >
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
          {futureCards.map((k) => (
            <article
              key={k}
              data-testid={`perm-future-${k.split(".").pop()}`}
              className="rounded-xl bg-white/[0.02] border border-white/10 p-3 flex items-start justify-between gap-3"
            >
              <div className="min-w-0">
                <div className="text-[13px] font-medium text-white truncate">
                  {t(k)}
                </div>
                <div className="text-[11px] text-slate-500 mt-0.5">
                  {t("perm.future.notConnected")}
                </div>
              </div>
              <button
                type="button"
                disabled
                aria-disabled="true"
                title={t("perm.future.comingSoon")}
                className="shrink-0 rounded-md px-2.5 py-1 text-[10px] text-slate-500 border border-white/10 bg-white/[0.02] cursor-not-allowed"
                data-testid={`perm-future-cta-${k.split(".").pop()}`}
              >
                {t("perm.future.comingSoon")}
              </button>
            </article>
          ))}
        </div>
      </Block>

      {/* 4. Permission Levels */}
      <Block
        testid="perm-block-levels"
        icon={<Layers size={14} className="text-purple-300" />}
        title={t("perm.levels.title")}
        description={t("perm.levels.explainer")}
      >
        <ol className="space-y-2">
          {levels.map((k, idx) => (
            <li
              key={k}
              className="flex items-start gap-3 rounded-lg bg-white/[0.02] border border-white/10 px-3 py-2"
            >
              <span className="text-[10px] font-mono text-purple-300 mt-0.5 w-5 shrink-0">
                L{idx + 1}
              </span>
              <span className="text-[13px] text-slate-200 leading-relaxed">
                {t(k)}
              </span>
            </li>
          ))}
        </ol>
      </Block>

      {/* 5. Security & Trust */}
      <div
        data-testid="perm-block-trust"
        className="rounded-2xl bg-cyan-500/[0.06] border border-cyan-500/25 p-5 flex items-start gap-3"
      >
        <Lock size={16} className="text-cyan-300 shrink-0 mt-0.5" />
        <div>
          <h3 className="font-display text-base font-semibold text-white">
            {t("perm.trust.title")}
          </h3>
          <p className="text-[13px] text-slate-300 mt-1 leading-relaxed">
            {t("perm.trust.copy")}
          </p>
        </div>
      </div>
    </section>
  );
}

function Block({ testid, icon, title, description, accent, children }) {
  const accentLine =
    accent === "amber"
      ? "border-l-amber-400/40"
      : "border-l-white/10";
  return (
    <div
      data-testid={testid}
      className={`pl-4 border-l-2 ${accentLine}`}
    >
      <div className="flex items-center gap-2 mb-1">
        {icon}
        <h3 className="font-display text-base font-semibold text-white">
          {title}
        </h3>
      </div>
      {description && (
        <p className="text-[12.5px] text-slate-400 mb-3 max-w-2xl leading-relaxed">
          {description}
        </p>
      )}
      {children}
    </div>
  );
}
